"use client";
import Translations from "@translate/components/ChatModal/SyncHandoff";
import { I18nUtil } from "@utils/i18n";

import { useEffect, useRef, useState } from "react";
import {
  isSyncStatusDone,
  resolveSyncGateView,
  useFinancialSyncStatus,
} from "@hooks/financialSyncStatus/useFinancialSyncStatus";
import {
  FINANCIAL_SYNC_WAIT_BUDGET_MS,
  isNoInvestConfirmed,
} from "@hooks/financialSyncStatus/syncStatusView";

import { Track } from "@/src/lib/tracking";
import { overlapAttributionParams } from "@features/etf-overlap/tracking/overlapAttribution";
// 资料包卡既有文案沿用其原 translate 模块（key 不变）
import ErrorTranslations from "@translate/screens/theme-pack/ThemePackConnectedPage";
import ContinueTranslations from "@translate/components/OnboardingWizard/shared/OnboardingCtaButton";
const ErrorI18n = I18nUtil.createScoped(ErrorTranslations);
const ContinueI18n = I18nUtil.createScoped(ContinueTranslations);

const I18n = I18nUtil.createScoped(Translations);

/**
 * 「连账户后延迟交接」的同步状态卡：Chat 以 `autoSubmit=false` + 协议行打开后，
 * 先等持仓同步，再把协议行真正发出。
 *
 * 从 `ThemePackChatHandoff` 泛化而来（EDG-561 FD-7）：状态机、轮询、60s 预算、`key={chatId:attempt}`
 * 重挂实现 Try again **原样搬入**，只把文案 / 事件名 / label 按 `kind` 参数化。
 * 资料包 `kind="theme_pack"` 的行为逐字不变。
 */
export type SyncHandoffKind = "theme_pack" | "etf_overlap";

export interface SyncHandoffProps {
  kind: SyncHandoffKind;
  chatId: string;
  /** theme_pack 才有；用于既有事件参数 theme_id */
  themeId?: string;
  onReady: () => Promise<boolean | { sent: boolean; reason?: string }>;
}

type HandoffResult = "sent" | "blocked" | "error" | "not_ready";

function HandoffAttempt({
  kind,
  onReady,
  onRetry,
  themeId,
  chatId,
  attempt,
}: SyncHandoffProps & { onRetry: () => void; attempt: number }) {
  const isOverlap = kind === "etf_overlap";
  const [phase, setPhase] = useState<
    "waiting" | "submitting" | "sent" | "failed"
  >("waiting");
  const [sendFailed, setSendFailed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [failedKind, setFailedKind] = useState<string | null>(null);
  const [noInvestConfirmed, setNoInvestConfirmed] = useState(false);
  const noInvestSeen = useRef<{ polls: number; since: number } | null>(null);
  const submitted = useRef(false);
  const { status } = useFinancialSyncStatus({
    enabled: phase === "waiting" && !timedOut,
    // 重叠分析：只有银行账户时不因「同步完成」而停轮询，等投资账户出现（review #123）
    holdOnNoInvest: isOverlap,
  });
  const gate = resolveSyncGateView(status);
  const ready =
    gate.kind === "invest-done" ||
    (gate.kind === "unknown" && isSyncStatusDone(status));
  // no-invest 可能只是账户目录索引延迟：重叠分析要连续读到才算数（资料包保持原行为）；invest-failed 是真失败
  const failed =
    gate.kind === "invest-failed" ||
    (gate.kind === "no-invest" && (!isOverlap || noInvestConfirmed));

  useEffect(() => {
    if (!isOverlap) {
      return;
    }
    if (gate.kind !== "no-invest") {
      noInvestSeen.current = null;
      setNoInvestConfirmed(false);
      return;
    }
    const seen = noInvestSeen.current ?? { polls: 0, since: Date.now() };
    seen.polls += 1;
    noInvestSeen.current = seen;
    if (isNoInvestConfirmed(seen.polls, Date.now() - seen.since)) {
      setNoInvestConfirmed(true);
    }
    // 每次轮询 `status` 都是新对象：以它的变化作为「又读到一次」
  }, [status]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setTimedOut(true),
      FINANCIAL_SYNC_WAIT_BUDGET_MS,
    );
    return () => window.clearTimeout(timer);
  }, []);

  // 事件名必须是字面量（`gen-openreplay-events.mjs` 静态扫描），所以两处各写一遍、不做名字透传。
  // 重叠分析的结果集没有 `blocked`：发送被拒绝一律记 `error`。
  const reportResult = (input: {
    trigger: "automatic" | "continue";
    syncState: string;
    result: HandoffResult;
    reason?: string;
  }) => {
    if (isOverlap) {
      Track.event("overlap_sync_result", {
        ...overlapAttributionParams(),
        trigger: input.trigger,
        result: input.result === "blocked" ? "error" : input.result,
        reason: input.reason,
        attempt,
      });
      return;
    }
    Track.event("pack_chat_handoff_result", {
      theme_id: themeId,
      chat_id: chatId,
      attempt,
      trigger: input.trigger,
      sync_state: input.syncState,
      result: input.result,
      reason: input.reason,
    });
  };

  const submit = (trigger: "automatic" | "continue") => {
    const syncState = gate.kind;
    if (submitted.current) {
      return;
    }
    submitted.current = true;
    setPhase("submitting");
    void onReady().then(
      (result) => {
        const sent = typeof result === "boolean" ? result : result.sent;
        submitted.current = sent;
        setSendFailed(!sent);
        setPhase(sent ? "sent" : "failed");
        reportResult({
          trigger,
          syncState,
          result: sent ? "sent" : "blocked",
          reason: typeof result === "boolean" ? "unknown" : result.reason,
        });
      },
      (error) => {
        submitted.current = false;
        setSendFailed(true);
        setPhase("failed");
        Track.error(error, {
          tag: isOverlap ? "SyncHandoff" : "ThemePackChatHandoff",
          msg: "send failed",
        });
        reportResult({
          trigger,
          syncState,
          result: "error",
          reason: "send_failed",
        });
      },
    );
  };

  useEffect(() => {
    if (phase !== "waiting" || submitted.current) {
      return;
    }
    if (failed || (timedOut && !ready)) {
      setPhase("failed");
      setFailedKind(failed ? gate.kind : "timeout");
      reportResult({
        trigger: "automatic",
        syncState: gate.kind,
        result: "not_ready",
        reason: failed ? gate.kind : "timeout",
      });
      return;
    }
    if (!ready) {
      return;
    }
    submit("automatic");
  }, [failed, onReady, phase, ready, timedOut]);

  if (phase === "sent") {
    return null;
  }
  return (
    <div
      className="mx-auto mt-8 flex w-full max-w-[900px] flex-col gap-3 rounded-3 border border-border_solid bg-background_solid_neutral_primary p-4 text-content_primary"
      role="status"
      aria-live="polite"
    >
      <p className="body_medium_m">
        {phase === "failed"
          ? sendFailed
            ? ErrorI18n.t("Couldn’t open Ed. Please try again.")
            : failedKind === "no-invest" && isOverlap
              ? "~I couldn’t find an investment account yet.~"
              : failedKind === "invest-failed" && isOverlap
                ? "~Syncing your account failed.~"
                : I18n.t("I couldn’t read your holdings yet.")
          : I18n.t("Reading your holdings…")}
      </p>
      {!sendFailed && (
        <p className="body_s text-content_secondary">
          {phase === "failed"
            ? failedKind === "no-invest" && isOverlap
              ? "~Connect a brokerage account, then try again.~"
              : I18n.t("Check your brokerage connection, then try again.")
            : isOverlap
              ? I18n.t(
                  "I’ll check which of your funds overlap as soon as the account is ready.",
                )
              : I18n.t(
                  "I’ll compare what you hold with your ranking as soon as the account is ready.",
                )}
        </p>
      )}
      {phase === "waiting" || phase === "failed" ? (
        <button
          type="button"
          data-openreplay-label={
            isOverlap ? "overlap_sync_continue" : "pack_chat_handoff_continue"
          }
          className="body_medium_s self-start text-content_brand_primary"
          onClick={() => submit("continue")}
        >
          {ContinueI18n.t("Continue")}
        </button>
      ) : null}
      {phase === "failed" ? (
        <button
          type="button"
          data-openreplay-label={
            isOverlap ? "overlap_sync_retry" : "pack_chat_handoff_retry"
          }
          className="body_medium_s self-start text-content_brand_primary"
          onClick={onRetry}
        >
          {I18n.t("Try again")}
        </button>
      ) : null}
    </div>
  );
}

export function SyncHandoff({
  kind,
  onReady,
  themeId,
  chatId,
}: SyncHandoffProps) {
  const [attempt, setAttempt] = useState(0);
  return (
    <HandoffAttempt
      kind={kind}
      themeId={themeId}
      chatId={chatId}
      attempt={attempt + 1}
      key={`${chatId}:${attempt}`}
      onReady={onReady}
      onRetry={() => setAttempt((value) => value + 1)}
    />
  );
}
